import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Minimize2, Maximize2 } from 'lucide-react';

const ModernChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Hi! 👋 How can I help you today?',
      sender: 'bot',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickReplies = [
    { text: '📋 Our Services', value: 'services' },
    { text: '🚁 DGCA RPTO', value: 'dgca' },
    { text: '📞 Contact Info', value: 'contact' },
    { text: '💰 Pricing', value: 'pricing' },
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getBotResponse = (userText) => {
    const text = userText.toLowerCase();
    
    if (text.includes('service') || text === 'services') {
      return '🎯 We offer comprehensive solutions:\n\n• Robotics & IoT Labs\n• Drone Lab Setup\n• DGCA RPTO Setup\n• 3D Printing Labs\n• AR/VR/AI Labs\n• Drone Soccer Arena\n\nWhich service interests you?';
    }
    if (text.includes('dgca') || text.includes('rpto')) {
      return '✈️ DGCA RPTO Setup:\n\n✓ Complete compliance documentation\n✓ Infrastructure planning\n✓ Simulator installation\n✓ Instructor training\n✓ Launch support\n\nWould you like a detailed proposal?';
    }
    if (text.includes('contact') || text === 'contact') {
      return '📞 Contact Us:\n\n📧 Uavgeosolutions@gmail.com\n📱 +91 9693809074\n📱 +91 9958831758\n📍 Greater Noida, Sector P3\n\nOr use our contact form!';
    }
    if (text.includes('price') || text.includes('pricing') || text.includes('cost')) {
      return '💰 Competitive Pricing:\n\nWe offer up to 40% cost savings compared to market rates!\n\nFor accurate pricing, we need to know:\n• Your requirements\n• Institution size\n• Specific services\n\nShall we schedule a consultation?';
    }
    if (text.includes('hello') || text.includes('hi') || text.includes('hey')) {
      return '👋 Hello! Welcome to UAV Geo Solutions.\n\nI can help you with:\n• Service information\n• Lab setup details\n• Pricing queries\n• Contact information\n\nWhat would you like to know?';
    }
    if (text.includes('thank')) {
      return '🙏 You\'re welcome! Feel free to reach out anytime. Have a great day!';
    }
    return '💬 Great question! For detailed information, I recommend:\n\n1. Browse our Services page\n2. Check our Gallery\n3. Contact our team directly\n\nHow else can I assist you?';
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      sender: 'user',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const botMessage = {
        id: Date.now() + 1,
        text: getBotResponse(inputValue),
        sender: 'bot',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const handleQuickReply = (value) => {
    setInputValue(value);
    setTimeout(() => handleSend(), 100);
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-primary via-cyan-500 to-indigo-500 rounded-full shadow-2xl flex items-center justify-center group"
          >
            <MessageCircle size={28} className="text-white" strokeWidth={2.5} />
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
              AI
            </div>
            {/* Pulse rings */}
            <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-20" />
            <div className="absolute inset-0 rounded-full bg-primary animate-pulse opacity-30" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? '60px' : '600px'
            }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] bg-gradient-to-br from-[#0a0e27] to-[#1a1f3a] rounded-3xl shadow-2xl overflow-hidden border border-primary/20"
            style={{ maxHeight: isMinimized ? '60px' : '600px' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-indigo-500 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <MessageCircle size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">UAV Assistant</h3>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-xs text-white/90">Online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all"
                >
                  {isMinimized ? <Maximize2 size={16} className="text-white" /> : <Minimize2 size={16} className="text-white" />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all"
                >
                  <X size={16} className="text-white" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            {!isMinimized && (
              <>
                <div className="h-[420px] overflow-y-auto p-4 space-y-3" style={{ scrollbarWidth: 'thin' }}>
                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] ${
                        msg.sender === 'user'
                          ? 'bg-gradient-to-br from-primary to-cyan-500 text-white rounded-2xl rounded-tr-sm'
                          : 'bg-white/10 text-white rounded-2xl rounded-tl-sm border border-white/10'
                      } px-4 py-3 shadow-lg`}>
                        <p className="text-sm whitespace-pre-line leading-relaxed">{msg.text}</p>
                        <span className="text-xs opacity-70 mt-1 block">{msg.time}</span>
                      </div>
                    </motion.div>
                  ))}
                  
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white/10 rounded-2xl rounded-tl-sm px-4 py-3 border border-white/10">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Replies */}
                {messages.length <= 2 && (
                  <div className="px-4 pb-3 flex flex-wrap gap-2">
                    {quickReplies.map((reply, idx) => (
                      <motion.button
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 }}
                        onClick={() => handleQuickReply(reply.value)}
                        className="text-xs px-3 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-full border border-primary/30 transition-all"
                      >
                        {reply.text}
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Input Area */}
                <div className="p-4 bg-black/20 border-t border-white/10">
                  <div className="flex space-x-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Type your message..."
                      className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim()}
                      className="w-12 h-12 bg-gradient-to-br from-primary to-cyan-500 hover:from-primary-hover hover:to-cyan-600 rounded-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-primary/50"
                    >
                      <Send size={20} className="text-white" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ModernChatbot;
